/**
 * Google Ads (gtag.js) Conversion Tracking
 *
 * Loads the Google Ads tag only when marketing consent is given.
 * Provides helpers to fire conversion events from funnel completions.
 *
 * Environment variables:
 * - NEXT_PUBLIC_GOOGLE_ADS_ID: Google Ads conversion ID (e.g., "AW-17963548865")
 */

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

export const GOOGLE_ADS_ID = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID || "";

/**
 * Check if Google Ads tracking is configured
 */
export function isGoogleAdsConfigured(): boolean {
  return Boolean(GOOGLE_ADS_ID);
}

/**
 * Fire a Google Ads conversion event
 *
 * @param conversionLabel - The conversion label from Google Ads (e.g., "AbCdEf...")
 * @param value - Optional conversion value in EUR
 */
export function trackGoogleAdsConversion(conversionLabel: string, value?: number): void {
  if (typeof window === "undefined" || !window.gtag || !GOOGLE_ADS_ID) return;

  const conversionData: Record<string, unknown> = {
    send_to: `${GOOGLE_ADS_ID}/${conversionLabel}`,
  };

  if (value !== undefined) {
    conversionData.value = value;
    conversionData.currency = "EUR";
  }

  window.gtag("event", "conversion", conversionData);
}

/**
 * Fire a generic gtag event (for remarketing audiences, etc.)
 */
export function trackGoogleAdsEvent(eventName: string, params?: Record<string, unknown>): void {
  if (typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", eventName, params);
}

/**
 * Conversion labels per funnel (set via env vars in Google Ads → Conversions)
 *
 * To get these labels:
 * 1. Go to Google Ads → Tools → Conversions
 * 2. Create a conversion action (e.g., "Schnellanfrage abgeschlossen")
 * 3. Choose "Use Google Tag Manager" or "Install tag manually"
 * 4. Copy the conversion label (the part after the slash in send_to)
 */
export const CONVERSION_LABELS = {
  quickRequest: process.env.NEXT_PUBLIC_GADS_CONV_QUICK_REQUEST || "",
  quickOffering: process.env.NEXT_PUBLIC_GADS_CONV_QUICK_OFFERING || "",
  /** Micro-conversion: buyer enters step 2 (completed step 1) */
  buyerFunnelStart: process.env.NEXT_PUBLIC_GADS_CONV_BUYER_FUNNEL_START || "",
  /** Micro-conversion: user opens Klara chatbot for the first time */
  klaraOpen: process.env.NEXT_PUBLIC_GADS_CONV_KLARA_OPEN || "",
  /** Micro-conversion: Klara creates an offering or request via tool */
  klaraConversion: process.env.NEXT_PUBLIC_GADS_CONV_KLARA_CONVERSION || "",
} as const;
